// DashboardMentor.tsx
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Users,
  Calendar,
  DollarSign,
  Star,
  Video,
  Mail,
  ArrowRight,
  UserPlus,
  Loader,
  TrendingUp,
  User,
  MessageCircle,
  ChevronRight,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { mentorshipApi, showError } from "../../lib";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

const COLORS = ["#10B981", "#F59E0B", "#EF4444", "#3B82F6"];

const DashboardMentor = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [hasProfile, setHasProfile] = useState<boolean | null>(null);
  const [sessions, setSessions] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [reviews, setReviews] = useState<any[]>([]);
  const [studentConversations, setStudentConversations] = useState<any[]>([]);
  const [studentUnreadCount, setStudentUnreadCount] = useState(0);

  useEffect(() => {
    fetchMentorDashboard();
  }, []);

  const fetchMentorDashboard = async () => {
    try {
      setLoading(true);

      const profileRes = await mentorshipApi
        .getMyMentorProfile()
        .catch(() => ({ success: false, data: null }));
      setHasProfile(profileRes.success && profileRes.data !== null);

      if (profileRes.success && profileRes.data) {
        const [sessionsRes, statsRes, conversationsRes] = await Promise.all([
          mentorshipApi
            .getMySessions()
            .catch(() => ({ success: false, data: [] })),
          mentorshipApi
            .getMentorStats()
            .catch(() => ({ success: false, data: null })),
          mentorshipApi
            .getConversations()
            .catch(() => ({ success: false, data: [] })),
        ]);

        if (sessionsRes.success)
          setSessions(sessionsRes.data?.slice(0, 5) || []);
        if (statsRes.success) setStats(statsRes.data);

        const reviewsRes = await mentorshipApi
          .getMentorReviews(profileRes.data._id)
          .catch(() => ({ success: false, data: [] }));
        if (reviewsRes.success) setReviews(reviewsRes.data?.slice(0, 5) || []);

        if (conversationsRes.success) {
          setStudentConversations(conversationsRes.data?.slice(0, 3) || []);
          const totalUnread =
            conversationsRes.data?.reduce(
              (acc: number, conv: any) => acc + (conv.unreadCount || 0),
              0,
            ) || 0;
          setStudentUnreadCount(totalUnread);
        }
      }
    } catch (err) {
      console.error("Failed to load mentor dashboard:", err);
    } finally {
      setLoading(false);
    }
  };

  const sessionStatusData = [
    { name: "Completed", value: stats?.completedSessions || 0 },
    { name: "Pending", value: stats?.pendingSessions || 0 },
    { name: "Cancelled", value: stats?.cancelledSessions || 0 },
  ];

  const weeklySessionsData = [
    { day: "Mon", sessions: 2 },
    { day: "Tue", sessions: 3 },
    { day: "Wed", sessions: 1 },
    { day: "Thu", sessions: 4 },
    { day: "Fri", sessions: 2 },
    { day: "Sat", sessions: 5 },
    { day: "Sun", sessions: 1 },
  ];

  const statsData = [
    {
      label: "Active Mentees",
      value: stats?.totalMentees || "0",
      icon: Users,
      color: "bg-blue-500",
      change: "+0",
    },
    {
      label: "Sessions This Month",
      value: stats?.totalSessionsThisMonth || "0",
      icon: Calendar,
      color: "bg-green-500",
      change: "+0",
    },
    {
      label: "Earnings",
      value: `${(stats?.totalEarnings || 0).toLocaleString()} XAF`,
      icon: DollarSign,
      color: "bg-purple-500",
      change: "+0%",
    },
    {
      label: "Avg. Rating",
      value: stats?.averageRating?.toFixed(1) || "0.0",
      icon: Star,
      color: "bg-yellow-500",
      change: "+0.0",
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px] bg-gradient-to-br from-orange-50 via-white to-orange-50/30">
        <Loader className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  if (hasProfile === false) {
    const fullName =
      user?.firstName && user?.lastName
        ? `${user.firstName} ${user.lastName}`
        : user?.username || "User";

    return (
      <div className="flex items-center justify-center min-h-[60vh] bg-gradient-to-br from-orange-50 via-white to-orange-50/30">
        <div className="max-w-md p-6 mx-4 text-center bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-center w-20 h-20 mx-auto mb-4 overflow-hidden bg-gradient-to-br from-orange-100 to-amber-100 rounded-full">
            {user?.avatar ? (
              <img
                src={user.avatar}
                alt={fullName}
                className="object-cover w-full h-full"
              />
            ) : (
              <User className="w-10 h-10 text-orange-500" />
            )}
          </div>
          <h2 className="mb-2 text-base font-bold text-gray-900">
            Welcome, {fullName}!
          </h2>
          <p className="mb-6 text-[11px] text-gray-500">
            You don't have a mentor profile yet. Create one to start accepting
            mentorship sessions and help students grow.
          </p>
          <button
            onClick={() => navigate("/dashboard/mentor/profile")}
            className="inline-flex items-center gap-2 px-4 py-2 text-[11px] font-medium text-white rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-md transition-all"
          >
            <UserPlus className="w-3.5 h-3.5" />
            Create Mentor Profile
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-4 bg-gradient-to-br from-orange-50 via-white to-orange-50/30 md:py-6">
      <div className="px-4 mx-auto max-w-7xl md:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 shadow-sm">
                  <Users className="w-4 h-4 text-white" />
                </div>
                <h1 className="text-lg font-bold text-gray-900 md:text-xl">
                  Mentor Dashboard
                </h1>
              </div>
              <p className="text-[10px] text-gray-500 ml-10 md:text-xs">
                Manage your mentees and sessions
              </p>
            </div>
            <Link
              to="/dashboard/mentor/sessions"
              className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-[10px] text-white rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-md transition-all md:text-xs md:px-4 md:py-2"
            >
              <Video className="w-3.5 h-3.5 md:w-4 md:h-4" />
              View All Sessions
            </Link>
          </div>
        </div>

        {/* Stats Grid - Responsive */}
        <div className="grid grid-cols-2 gap-3 mb-5 sm:grid-cols-2 lg:grid-cols-4 lg:gap-4">
          {statsData.map((stat, i) => (
            <div
              key={i}
              className="bg-white rounded-xl p-3 shadow-sm border border-gray-100"
            >
              <div className="flex items-center justify-between mb-2">
                <div
                  className={`w-8 h-8 ${stat.color} bg-opacity-10 rounded-lg flex items-center justify-center`}
                >
                  <stat.icon
                    className={`w-4 h-4 ${stat.color.replace("bg-", "text-")}`}
                  />
                </div>
                <span className="text-sm font-bold text-gray-900">
                  {stat.value}
                </span>
              </div>
              <p className="text-[9px] text-gray-500 md:text-[10px]">
                {stat.label}
              </p>
              <p className="text-[8px] text-green-600 mt-1 md:text-[9px]">
                {stat.change} this month
              </p>
            </div>
          ))}
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 gap-4 mb-5 lg:grid-cols-3 lg:gap-5">
          {/* Weekly Sessions Line Chart */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 md:p-4">
            <h2 className="text-[11px] font-semibold text-gray-900 mb-2 md:text-sm md:mb-3">
              Weekly Sessions
            </h2>
            <div className="h-40 md:h-48">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={weeklySessionsData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="day"
                    tick={{ fontSize: 9 }}
                    stroke="#9ca3af"
                  />
                  <YAxis tick={{ fontSize: 9 }} stroke="#9ca3af" />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="sessions"
                    stroke="#F59E0B"
                    strokeWidth={2}
                    dot={{ fill: "#F59E0B", r: 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Session Status Pie Chart */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 md:p-4">
            <h2 className="text-[11px] font-semibold text-gray-900 mb-2 md:text-sm md:mb-3">
              Session Status
            </h2>
            <div className="h-40 md:h-48">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={sessionStatusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={30}
                    outerRadius={50}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    <Cell fill="#10B981" />
                    <Cell fill="#F59E0B" />
                    <Cell fill="#EF4444" />
                  </Pie>
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 9 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Reviews Summary */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 md:p-4">
            <h2 className="text-[11px] font-semibold text-gray-900 mb-2 md:text-sm md:mb-3">
              Reviews Summary
            </h2>
            <div className="space-y-2">
              <div className="flex items-center justify-between p-2 rounded-lg bg-green-50">
                <div className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-yellow-500" />
                  <span className="text-[11px] text-gray-700">
                    Average Rating
                  </span>
                </div>
                <span className="text-[11px] font-semibold text-green-600">
                  {stats?.averageRating?.toFixed(1) || "0.0"}
                </span>
              </div>
              <div className="flex items-center justify-between p-2 rounded-lg bg-blue-50">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-blue-600" />
                  <span className="text-[11px] text-gray-700">
                    Total Reviews
                  </span>
                </div>
                <span className="text-[11px] font-semibold text-blue-600">
                  {stats?.totalReviews || 0}
                </span>
              </div>
              <div className="flex items-center justify-between p-2 rounded-lg bg-purple-50">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-purple-600" />
                  <span className="text-[11px] text-gray-700">
                    Total Sessions
                  </span>
                </div>
                <span className="text-[11px] font-semibold text-purple-600">
                  {stats?.totalSessions || 0}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-5">
          {/* Upcoming Sessions */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 md:p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-[11px] font-bold text-gray-900 md:text-sm">
                Upcoming Sessions
              </h2>
              <Link
                to="/dashboard/mentor/sessions"
                className="text-[10px] text-orange-600 hover:underline"
              >
                View All
              </Link>
            </div>
            <div className="space-y-2">
              {(sessions.length > 0 ? sessions : []).map((session: any, i) => {
                const student = session.student || session.mentee;
                const studentName =
                  student?.firstName && student?.lastName
                    ? `${student.firstName} ${student.lastName}`
                    : student?.name || student?.email || "Student";
                const studentAvatar = student?.avatar;

                return (
                  <div
                    key={i}
                    className="flex items-center gap-2 p-2 rounded-lg bg-gray-50"
                  >
                    <div className="flex items-center justify-center w-8 h-8 overflow-hidden rounded-full bg-orange-100 shrink-0">
                      {studentAvatar ? (
                        <img
                          src={studentAvatar}
                          alt=""
                          className="object-cover w-full h-full"
                        />
                      ) : (
                        <Users className="w-4 h-4 text-orange-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-medium text-gray-900 truncate">
                        {studentName}
                      </p>
                      <p className="text-[9px] text-gray-500 truncate">
                        {session.topic || session.notes}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-[9px] text-gray-600 whitespace-nowrap">
                        {session.scheduledAt
                          ? new Date(session.scheduledAt).toLocaleDateString()
                          : "TBD"}
                      </p>
                      <span
                        className={`text-[8px] px-1.5 py-0.5 rounded-full ${
                          session.status === "confirmed"
                            ? "bg-green-100 text-green-700"
                            : session.status === "completed"
                              ? "bg-blue-100 text-blue-700"
                              : session.status === "cancelled"
                                ? "bg-red-100 text-red-700"
                                : "bg-yellow-100 text-yellow-700"
                        }`}
                      >
                        {session.status || "pending"}
                      </span>
                    </div>
                  </div>
                );
              })}
              {sessions.length === 0 && (
                <div className="py-6 text-center">
                  <Calendar className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  <p className="text-[10px] text-gray-500">
                    No upcoming sessions
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Student Messages */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 md:p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-[11px] font-bold text-gray-900 md:text-sm">
                Student Messages
              </h2>
              <Link
                to="/dashboard/mentor/messages"
                className="text-[10px] text-orange-600 hover:underline"
              >
                View All
              </Link>
            </div>
            <div className="space-y-2">
              {studentConversations.length > 0 ? (
                studentConversations.map((conv: any, i: number) => (
                  <button
                    key={i}
                    onClick={() =>
                      navigate(
                        `/dashboard/mentor/messages?user=${conv.user._id}`,
                      )
                    }
                    className="flex items-center justify-between w-full p-2 text-left transition-colors rounded-lg bg-orange-50 hover:bg-orange-100"
                  >
                    <div className="flex items-center gap-2">
                      <div className="flex items-center justify-center w-8 h-8 overflow-hidden rounded-full bg-orange-100 shrink-0">
                        {conv.user?.avatar ? (
                          <img
                            src={conv.user.avatar}
                            alt=""
                            className="object-cover w-full h-full rounded-full"
                          />
                        ) : (
                          <Users className="w-4 h-4 text-orange-600" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="text-[11px] font-medium text-gray-900 truncate max-w-[100px]">
                          {conv.user?.name || "Student"}
                        </p>
                        <p className="text-[9px] text-gray-500 truncate max-w-[100px]">
                          {conv.lastMessage || "No messages"}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-0.5">
                      {conv.unreadCount > 0 && (
                        <span className="px-1.5 py-0.5 text-[8px] font-bold text-white bg-orange-500 rounded-full">
                          {conv.unreadCount}
                        </span>
                      )}
                      {conv.lastMessageAt && (
                        <span className="text-[8px] text-gray-400">
                          {new Date(conv.lastMessageAt).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </button>
                ))
              ) : (
                <div className="py-6 text-center">
                  <MessageCircle className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  <p className="text-[10px] text-gray-500">No messages yet</p>
                  <p className="text-[8px] text-gray-400">
                    Students will message you after booking sessions
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Recent Reviews */}
        <div className="mt-4 bg-white rounded-xl shadow-sm border border-gray-100 p-3 md:p-4 md:mt-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-[11px] font-bold text-gray-900 md:text-sm">
              Recent Reviews
            </h2>
            <Link
              to="/mentor/reviews"
              className="text-[10px] text-orange-600 hover:underline"
            >
              View All
            </Link>
          </div>
          <div className="space-y-2">
            {reviews.length > 0 ? (
              reviews.map((review: any, i) => (
                <div key={i} className="p-3 rounded-lg bg-gray-50">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-1">
                      {[...Array(5)].map((_, j) => (
                        <Star
                          key={j}
                          className={`w-2.5 h-2.5 ${j < review.rating ? "text-yellow-500 fill-yellow-500" : "text-gray-300"}`}
                        />
                      ))}
                    </div>
                    <span className="text-[8px] text-gray-400">
                      {new Date(review.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-[10px] text-gray-700 line-clamp-2">
                    {review.comment || "Great session!"}
                  </p>
                  <p className="mt-1 text-[9px] font-medium text-gray-600">
                    —{" "}
                    {review.student?.firstName && review.student?.lastName
                      ? `${review.student.firstName} ${review.student.lastName}`
                      : review.student?.name ||
                        review.student?.email ||
                        "Anonymous"}
                  </p>
                </div>
              ))
            ) : (
              <div className="py-6 text-center">
                <Star className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p className="text-[10px] text-gray-500">No reviews yet</p>
              </div>
            )}
          </div>
        </div>

        {/* Stats Summary Footer */}
        <div className="grid grid-cols-3 gap-3 mt-5">
          <div className="bg-white rounded-xl p-2 text-center border border-gray-100 shadow-sm">
            <div className="flex items-center justify-center w-6 h-6 mx-auto mb-0.5 rounded-lg bg-green-100">
              <TrendingUp className="w-3 h-3 text-green-600" />
            </div>
            <p className="text-[10px] font-bold text-gray-900">
              {stats?.totalSessions || 0}
            </p>
            <p className="text-[7px] text-gray-500">Total Sessions</p>
          </div>
          <div className="bg-white rounded-xl p-2 text-center border border-gray-100 shadow-sm">
            <div className="flex items-center justify-center w-6 h-6 mx-auto mb-0.5 rounded-lg bg-blue-100">
              <Users className="w-3 h-3 text-blue-600" />
            </div>
            <p className="text-[10px] font-bold text-gray-900">
              {stats?.totalMentees || 0}
            </p>
            <p className="text-[7px] text-gray-500">Active Mentees</p>
          </div>
          <div className="bg-white rounded-xl p-2 text-center border border-gray-100 shadow-sm">
            <div className="flex items-center justify-center w-6 h-6 mx-auto mb-0.5 rounded-lg bg-purple-100">
              <Star className="w-3 h-3 text-purple-600" />
            </div>
            <p className="text-[10px] font-bold text-gray-900">
              {stats?.averageRating?.toFixed(1) || "0.0"}
            </p>
            <p className="text-[7px] text-gray-500">Rating</p>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .animate-spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default DashboardMentor;
